import { useState } from 'react'

import ArrowLeftIcon from '@heroicons/react/outline/ArrowLeftIcon'
import ArrowRightIcon from '@heroicons/react/outline/ArrowRightIcon'
import clsx from 'clsx'

import { PlanName } from '@tribeplatform/gql-client/types'
import { useActivityLogs, useNetwork } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { List } from '@tribeplatform/react-ui-kit/Layout'

import { UpgradeBanner } from '../Admin/UpgradeBanner'
import { AuditLogRow, parseLog } from './AuditLogRow'

export const AuditLogListLoading = ({ count = 10 }) => {
  return (
    <div className="relative animate-pulse">
      <List spacing="md">
        {[...Array(count)].map((_e, idx) => (
          <List.Item key={_e} className="flex items-center space-x-3 h-[58px]">
            <div className="rounded-full bg-surface-300 h-9 w-9" />
            <div
              className={clsx(
                'h-5 bg-surface-300 rounded-full flex',
                idx % 2 === 0 ? 'w-1/3' : 'w-1/4',
              )}
            />
          </List.Item>
        ))}
      </List>
      <div className="absolute -z-10 w-[2px] ml-[1px] top-8 left-4 bottom-8 bg-surface-300" />
    </div>
  )
}

export const generateAuditLogQuery = ({
  filterBy,
  from,
  limit = 100,
  offset = null,
}) => {
  let where = ''
  Object.keys(filterBy).forEach(key => {
    if (filterBy[key]) where += `and ${key}='${filterBy[key]}' `
  })
  return `select id, createdAt, noun, verb, appId, actorId, memberId, spaceId, postId, object ${
    from ? `timeframe from ${from} ` : ''
  }where noun!='TRACKER' ${where}order by createdAt desc limit ${limit}${
    offset ? ` offset ${offset}` : ''
  }`
}

const ROWS_PER_PAGE = 30

export const AuditLogList = ({
  spaceId,
  actorId,
  postId,
  memberId,
  noun,
  from,
}: {
  spaceId?: string
  actorId?: string
  postId?: string
  memberId?: string
  noun?: string
  from?: number
}) => {
  const [page, setPage] = useState(1)
  const filterBy = {
    spaceId,
    actorId,
    postId,
    memberId,
    noun,
    verb: undefined,
  }

  const { data: activityLogs, isLoading } = useActivityLogs({
    variables: {
      query: generateAuditLogQuery({
        filterBy,
        from,
        limit: ROWS_PER_PAGE,
        offset: (page - 1) * ROWS_PER_PAGE,
      }),
    },
    fields: {
      entities: {
        actor: 'basic',
        member: 'basic',
        app: 'basic',
        post: {
          space: 'basic',
        },
        space: 'basic',
        tag: 'basic',
      },
    },
    useQueryOptions: {
      refetchOnMount: 'always',
    },
  })
  const { data: network } = useNetwork()
  const plan = network?.subscriptionPlan
  const upgradeMessage = {
    [PlanName.BASIC]: '24 hours',
    [PlanName.PLUS]: '24 hours',
    [PlanName.PREMIUM]: '10 days',
    [PlanName.ENTERPRISE]: null,
  }

  let content
  if (isLoading) content = <AuditLogListLoading count={ROWS_PER_PAGE} />
  else if (activityLogs?.length)
    content = (
      <div className="relative">
        <List spacing="md">
          {activityLogs?.map(log => {
            const parsedLog = parseLog(log)
            return <AuditLogRow key={parsedLog?.payload?.id} log={parsedLog} />
          })}
        </List>
        <div className="absolute -z-10 w-[2px] ml-[1px] top-8 left-4 bottom-8 bg-surface-200" />
      </div>
    )
  else
    content = (
      <div className="py-5 text-center">
        No audit logs available in the selected timeframe.
      </div>
    )

  return (
    <div className="flex flex-col space-y-5">
      {content}
      <div className="flex space-x-3 items-center text-center mt-3">
        <Button
          fullWidth
          size="lg"
          variant="outline"
          className="flex-1"
          disabled={page <= 1}
          onClick={() => {
            setPage(page - 1)
          }}
          leadingIcon={<ArrowLeftIcon />}
        >
          Previous
        </Button>
        <div className="flex-1">Page {page}</div>
        <Button
          fullWidth
          size="lg"
          variant="outline"
          className="flex-1"
          loading={isLoading}
          onClick={() => {
            setPage(page + 1)
          }}
          disabled={activityLogs?.length < ROWS_PER_PAGE}
          trailingIcon={<ArrowRightIcon />}
        >
          Next
        </Button>
      </div>
      {upgradeMessage[plan?.name] ? (
        <UpgradeBanner>
          Your plan supports {upgradeMessage[plan?.name]} of audit logs
          retention
        </UpgradeBanner>
      ) : null}
    </div>
  )
}
